import _ from 'lodash';
import { determineIdealUnits, getDurationTimeUnit } from '@/datetime/dateTime.utilities';
import { LOW_PASS_FILTER_CUTOFF, TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DURATION_TIME_UNITS } from '@/main/app.constants';
import type { BaseToolPersistedState, ToolItem } from '@/toolSelection/baseTool.store';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import type { FrontendDuration } from '@/services/systemConfiguration.types';

type DurationLike = Partial<FrontendDuration>;

interface RatePayload {
  rate: FrontendDuration;
}

interface HighPassPayload {
  highPass: DurationLike;
}

interface LowPassPayload {
  lowPass: DurationLike;
}

export class FftStore extends BaseToolStore {
  type = TREND_TOOLS.FFT_TABLE;
  static readonly storeName = 'sqFftStore';
  parameterDefinitions = {
    signalToAggregate: { predicate: ['name', 'signalToAggregate'] },
  };

  initialize() {
    this.state = this.immutable(
      _.assign({}, BASE_TOOL_COMMON_PROPS, this.parameterDefinitions, {
        rateType: true,
        rate: LOW_PASS_FILTER_CUTOFF,
        outputUnits: getDurationTimeUnit('min', DURATION_TIME_UNITS)?.unit[0] || 'min',
        highPass: LOW_PASS_FILTER_CUTOFF,
        lowPass: LOW_PASS_FILTER_CUTOFF,
        useHighPass: false,
        useLowPass: false,
      }),
    );
  }

  get signalToAggregate() {
    return this.state.get('signalToAggregate');
  }

  get rateType(): boolean {
    return this.state.get('rateType');
  }

  get rate(): DurationLike {
    return this.state.get('rate');
  }

  get outputUnits(): string {
    return this.state.get('outputUnits');
  }

  get highPass(): DurationLike {
    return this.state.get('highPass');
  }

  get lowPass(): DurationLike {
    return this.state.get('lowPass');
  }

  get useHighPass(): boolean {
    return this.state.get('useHighPass');
  }

  get useLowPass(): boolean {
    return this.state.get('useLowPass');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): FftPersistedState {
    return {
      ...this.getBasePersistedState(),
      signalToAggregate: this.state.get('signalToAggregate') as ToolItem | null | undefined,
      rateType: Boolean(this.state.get('rateType')),
      rate: this.state.get('rate') as DurationLike,
      outputUnits: this.state.get('outputUnits') as string,
      highPass: this.state.get('highPass') as DurationLike,
      lowPass: this.state.get('lowPass') as DurationLike,
      useHighPass: Boolean(this.state.get('useHighPass')),
      useLowPass: Boolean(this.state.get('useLowPass')),
    };
  }

  /**
   * Sets the fft panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<FftPersistedState>) {
    this.applyBasePersistedState(dehydratedState);

    if ('signalToAggregate' in dehydratedState) {
      this.state.set('signalToAggregate', dehydratedState.signalToAggregate);
    }
    if ('rateType' in dehydratedState && dehydratedState.rateType !== undefined) {
      this.state.set('rateType', dehydratedState.rateType);
    }
    if ('rate' in dehydratedState && dehydratedState.rate !== undefined) {
      this.state.set('rate', dehydratedState.rate);
    }
    if ('outputUnits' in dehydratedState && dehydratedState.outputUnits !== undefined) {
      this.state.set('outputUnits', dehydratedState.outputUnits);
    }
    if ('highPass' in dehydratedState && dehydratedState.highPass !== undefined) {
      this.state.set('highPass', dehydratedState.highPass);
    }
    if ('lowPass' in dehydratedState && dehydratedState.lowPass !== undefined) {
      this.state.set('lowPass', dehydratedState.lowPass);
    }
    if ('useHighPass' in dehydratedState && dehydratedState.useHighPass !== undefined) {
      this.state.set('useHighPass', dehydratedState.useHighPass);
    }
    if ('useLowPass' in dehydratedState && dehydratedState.useLowPass !== undefined) {
      this.state.set('useLowPass', dehydratedState.useLowPass);
    }
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    /**
     * Sets the rate type flag
     *
     * @param {Object} payload - Object container
     * @param {Bolean} payload.rateType - True if period rate type, false if frequency
     */
    FFT_SET_RATE_TYPE: (payload: { rateType: boolean }) => {
      this.state.set('rateType', payload.rateType);
    },

    /**
     * Sets the sampling rate
     *
     * @param {Object} payload - Object container
     * @param {String} payload.rate - the rate to sample at
     */
    FFT_SET_RATE: (payload: RatePayload) => {
      let rate = payload.rate;

      // If the rate is undefined, populate the display rate.  This usually occurs when the user selects a series,
      // as the payload units are generally not human friendly

      const currentRate = this.state.get('rate') as DurationLike;
      if (_.isUndefined(currentRate?.value)) {
        rate = determineIdealUnits(payload.rate);
      }

      this.state.set('rate', rate);
    },

    /**
     * Sets the output units of the fft
     *
     * @param {Object} payload - Object container
     * @param {String} payload.outputUnits - The units to format the bucket labels with
     */
    FFT_SET_OUTPUT_UNITS: (payload: { outputUnits: string }) => {
      this.state.set('outputUnits', payload.outputUnits);
    },

    /**
     * Sets the high pass filter
     *
     * @param {Object} payload - Object container
     * @param {String} payload.highPass - the high pass filter
     */
    FFT_SET_FILTER_HIGH: (payload: HighPassPayload) => {
      this.state.set('highPass', payload.highPass);
    },

    /**
     * Sets the low pass filter
     *
     * @param {Object} payload - Object container
     * @param {String} payload.lowPass - the low pass filter
     */
    FFT_SET_FILTER_LOW: (payload: LowPassPayload) => {
      this.state.set('lowPass', payload.lowPass);
    },

    /**
     * Sets the use of the high pass filter
     * @param {Object} payload - Object container
     * @param {Bolean} payload.useHighPass - the flag for use of high pass filter
     */
    FFT_SET_USE_FILTER_HIGH: (payload: { useHighPass: boolean }) => {
      this.state.set('useHighPass', payload.useHighPass);
    },

    /**
     * Sets the use of the low pass filter
     *
     * @param {Object} payload - Object container
     * @param {Bolean} payload.useLowPass - the flag for use of low pass filter
     */
    FFT_SET_USE_FILTER_LOW: (payload: { useLowPass: boolean }) => {
      this.state.set('useLowPass', payload.useLowPass);
    },
  };
}
interface FftPersistedState extends BaseToolPersistedState {
  signalToAggregate?: ToolItem | null;
  rateType: boolean;
  rate: DurationLike;
  outputUnits: string;
  highPass: DurationLike;
  lowPass: DurationLike;
  useHighPass: boolean;
  useLowPass: boolean;
}
